import NCBISvg from '@components/app/ListingPage/NCBISvg'
import { type PubmedArticleType } from '@components/app/ListingPage/TrialResearchFindingsSection'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faExternalLink, faLightbulb } from '@fortawesome/pro-regular-svg-icons'

const ArticleWithFinding = ({
  article,
  hidden = false,
}: {
  article: PubmedArticleType
  hidden?: boolean
}) => {
  return (
    <div
      className={classMerge(
        'scroll-mt-24 rounded-lg border border-neutral100 bg-white p-4',
        hidden ? 'hidden' : '',
      )}
      id={`research-${article.pmId}`}
    >
      <FindingsSection article={article} />

      <div className=' mt-4 flex flex-col gap-2 md:flex-row md:justify-between'>
        <div className='flex'>
          <div className='flex size-12 max-h-12 max-w-14 items-center justify-center rounded-full bg-[#336699] p-1'>
            <NCBISvg />
          </div>
          <div className='ml-4 max-h-12'>
            <span className='block  text-[length:0.875rem] font-bold leading-[140%] text-black md:text-[length:0.875rem] md:leading-[140%]'>
              NCBI (Pubmed)
            </span>
            <LinkToPubmedArticle pmId={article.pmId} />
          </div>
        </div>
        <PaperTitleAndAuthors article={article} />
      </div>
    </div>
  )
}

const FindingsSection = ({ article }: { article: PubmedArticleType }) => (
  <div className='flex flex-col items-start gap-2 rounded-lg bg-primary50 p-4'>
    {article.keyResearchFindings.map((finding, i) => (
      <div className='flex items-center' key={i}>
        <Icon className='mr-4' icon={faLightbulb} />
        <span className='block text-[length:0.875rem] font-semibold leading-[140%] text-neutral700 md:text-[length:0.875rem] md:leading-[140%]'>
          {finding}
        </span>
      </div>
    ))}
  </div>
)

const PaperTitleAndAuthors = ({ article }: { article: PubmedArticleType }) => {
  const publishedYear = article.dateRevised
    ? new Date(article.dateRevised).getFullYear()
    : ''
  return (
    <div className='mb-2 md:max-w-[50%] md:text-right'>
      <span className='line-clamp-1 break-all pb-[2px] text-[length:0.8125rem] font-semibold leading-[140%] text-black md:text-[length:0.8125rem] md:leading-[140%]'>
        {article.articleTitle}
      </span>

      <span className='line-clamp-1 break-words text-[length:0.8125rem] font-extralight leading-[140%] text-neutral500 md:text-[length:0.8125rem] md:leading-[140%]'>
        <TruncatedListOfAuthors authors={article.authors} />[{publishedYear}]
      </span>
    </div>
  )
}

const TruncatedListOfAuthors = ({
  authors,
  maxAuthors = 3,
}: {
  authors: string[]
  maxAuthors?: number
}) => {
  if (authors.length === 0) {
    return <></>
  }

  const needEtAl = authors.length > maxAuthors

  const authorsToDisplay = authors.slice(0, maxAuthors)

  return (
    <>
      {authorsToDisplay.join(', ')}
      {needEtAl ? ', et al.' : ''}
    </>
  )
}

const LinkToPubmedArticle = ({ pmId }: { pmId: number }) => (
  <a
    className='hover:underline'
    href={`https://pubmed.ncbi.nlm.nih.gov/${pmId}`}
    rel='noopener noreferrer'
    target='_blank'
  >
    <div className='inline-flex items-center font-light text-neutral300 '>
      <span className='text-[length:0.875rem] font-medium leading-[140%] text-neutral500 hover:underline md:text-[length:0.875rem] md:leading-[140%]'>
        pubmed.ncbi.nlm.nih.gov
      </span>
      <Icon className='ml-1 size-3' icon={faExternalLink} />
    </div>
  </a>
)

export default ArticleWithFinding
